import { useState, useEffect, useCallback } from 'react';
import { Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { useForm, useFormState, useActions } from 'utils/hooks';
import { getVisibleCompanies } from 'components/visibility-settings/helpers';
import Drawer, { DrawerHeader, DrawerFooter } from 'common/components/drawer';
import { DrawerState } from 'common/entities/drawer/DrawerTypes';

import { FormDrawer } from 'common/components/drawer';
import { FormBody } from 'common/components/drawer';
import VisibilitySettings from 'components/visibility-settings';
import Spinner from 'components/general/Spinner';
import Edit from './edit';
import config from './config';

import { selectListOptionsFromStore } from 'store/lists/selectors';
import { selectActiveOrganizations, selectDefaultStatus } from 'store/event-types/selectors';

import * as eventTypesActions from 'store/event-types/actions';
import * as listActions from 'store/lists/actions';

const EventTypesDrawer = ({ drawer, active, refetchData, drawerType }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const { formState, loadValues, resetForm, collectValues } = useForm(config);
  const { subStates } = useFormState(formState);

  const organizationsState = subStates('organizations');

  const activeOrganizations = useSelector(selectActiveOrganizations);
  const defaultStatus = useSelector(selectDefaultStatus);

  const [
    createEventType,
    updateEventType,
    getEventTypesVisibility,
    updateEventTypesVisibility,
    fetchListOptions
  ] = useActions([
    eventTypesActions.createEventType,
    eventTypesActions.updateEventType,
    eventTypesActions.getEventTypesVisibility,
    eventTypesActions.updateEventTypesVisibility,
    listActions.fetchListOptions
  ]);

  const organizations = useSelector(state => selectListOptionsFromStore(state, 'organizations'));

  const initVisibility = useCallback(async () => {
    try {
      setLoading(true);

      await getEventTypesVisibility({ event_type_id: active?.id }).unwrap();
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  }, [getEventTypesVisibility, active?.id]);

  const onSubmit = async () => {
    const values = collectValues();

    try {
      const { organizations, ...rest } = values;

      const params = { ...rest };

      setIsSubmitting(true);

      if (drawerType === 'visibility') {
        const activeOrganizations =
          (organizations || [])?.filter(organization => organization.active) || [];

        const activeOrganizationIds = activeOrganizations?.map(o => o.id);
        const allHidden = activeOrganizations.length === 0;

        const params = {
          default: 'hide',

          organization_ids: allHidden ? [] : activeOrganizationIds
        };

        await updateEventTypesVisibility({ event_type_id: active.id, ...params });
      }
      if (drawerType === 'editIndex') {
        if (active?.id) {
          await updateEventType({ event_type_id: active?.id, ...params });
        } else {
          await createEventType(params);
        }
      }

      drawer.close();
      refetchData();
      setIsSubmitting(false);
    } catch (e) {
      console.error(e);

      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (drawer.isOpen && active) {
      if (drawerType === 'visibility') {
        const parsedOrganizations =
          defaultStatus === 'show' || activeOrganizations?.length
            ? getVisibleCompanies(organizations, activeOrganizations, defaultStatus)
            : organizations;

        loadValues({ organizations: parsedOrganizations });
      } else if (drawerType === 'editIndex') {
        loadValues(active);
      }
    } else {
      resetForm();
    }
  }, [
    drawer.isOpen,
    loadValues,
    organizations,
    drawerType,
    active,
    resetForm,
    activeOrganizations,
    defaultStatus
  ]);

  useEffect(() => {
    if (drawer.isOpen && drawerType === 'visibility' && active) {
      fetchListOptions('organizations');
      initVisibility();
    }
  }, [fetchListOptions, initVisibility, drawer.isOpen, drawerType, active]);

  return (
    <Drawer {...drawer} className="table-drawer">
      <DrawerHeader className="border-bottom pb-4">
        {active && drawerType === 'visibility' ? (
          <div className="d-flex flex-column">
            <span>Visibility Settings</span>
            <span className="fs-12 fw-bold">{active.name}</span>
          </div>
        ) : active ? (
          'Edit ' + active.name
        ) : (
          'Create new Event Type'
        )}
      </DrawerHeader>

      <FormDrawer>
        <FormBody>
          {active && drawerType === 'visibility' && loading ? (
            <Spinner />
          ) : active && drawerType === 'visibility' && !loading ? (
            <VisibilitySettings
              text="Select in which companies, this event type is active:"
              organizationsState={organizationsState}
            />
          ) : (
            <Edit formState={formState} />
          )}
        </FormBody>
      </FormDrawer>

      <DrawerFooter className="d-flex justify-content-end p-3">
        <Button
          color="cancel"
          className="px-0 py-1 me-4"
          onClick={() => drawer.close()}
          disabled={isSubmitting}
        >
          CANCEL
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting} color="primary" className="px-4">
          SAVE
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

EventTypesDrawer.propTypes = {
  drawer: DrawerState,
  active: PropTypes.shape({
    active: PropTypes.bool,
    color: PropTypes.string,
    for_performance_reports: PropTypes.bool,
    id: PropTypes.number,
    label: PropTypes.string,
    name: PropTypes.string
  }),
  refetchData: PropTypes.func,
  drawerType: PropTypes.oneOf(['visibility', 'editIndex'])
};

export default EventTypesDrawer;
